import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrEvent } from '@nostrify/nostrify';

interface UseRepoReactionsOptions {
  /** The repository event to fetch reactions for */
  repoEventId: string;
  /** Whether the query should run. Default: true */
  enabled?: boolean;
}

export interface ReactionEvent extends NostrEvent {
  kind: 7;
}

/**
 * Fetch NIP-25 reactions (kind 7) for a repository announcement event.
 *
 * This hook queries for all reactions to a specific repository event,
 * allowing users to like, love, or react with custom emojis to repositories.
 */
export function useRepoReactions(options: UseRepoReactionsOptions) {
  const { repoEventId, enabled = true } = options;
  const { nostr } = useNostr();

  return useQuery<ReactionEvent[]>({
    queryKey: ['repo-reactions', repoEventId],
    enabled: enabled && !!repoEventId,
    queryFn: async ({ signal }) => {
      const events = await nostr.query(
        [
          {
            kinds: [7],
            '#e': [repoEventId],
            limit: 500,
          },
        ],
        {
          signal: AbortSignal.any([signal, AbortSignal.timeout(3000)]),
        }
      );

      return events.filter((e): e is ReactionEvent => e.kind === 7);
    },
  });
}

/**
 * Get aggregated reaction counts from a list of reactions
 */
export function aggregateReactions(reactions: ReactionEvent[]): Record<string, number> {
  const counts: Record<string, number> = {};

  for (const reaction of reactions) {
    const emoji = reaction.content || '+';
    counts[emoji] = (counts[emoji] || 0) + 1;
  }

  return counts;
}

/**
 * Check if the current user has reacted to a repository
 */
export function hasUserReacted(reactions: ReactionEvent[], userPubkey?: string): boolean {
  if (!userPubkey) return false;
  return reactions.some((r) => r.pubkey === userPubkey);
}
